import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import {
  updateClinicSettings, updateSchedulingSettings, updateBillingSettings, updateNotificationSettings,
  updateDisplaySettings, getDoctors, updateDoctor,
} from "../../api/api";
import { getSettingsThunk } from "../../store/slice/settings-slice";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import ProviderSelect from "../../components/ProviderSelect";

const DAYS = [
  { value: 0, label: "Sun" }, { value: 1, label: "Mon" }, { value: 2, label: "Tue" },
  { value: 3, label: "Wed" }, { value: 4, label: "Thu" }, { value: 5, label: "Fri" }, { value: 6, label: "Sat" },
];

const NOTIFICATION_TYPES = [
  { key: "confirmation", label: "Booking confirmations" },
  { key: "reminder", label: "Reminders" },
  { key: "delay", label: "Delay notices" },
  { key: "leave", label: "Leave notices" },
  { key: "invoice", label: "Invoices" },
  { key: "prescription", label: "Prescriptions" },
  { key: "digest", label: "Doctor daily digest (1hr before first appointment)" },
];

function SectionCard({ title, description, children }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="text-sm font-semibold">{title}</h3>
      {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

function ClinicTab({ settings, onSaved }) {
  const [form, setForm] = useState(settings.clinic);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await updateClinicSettings(form);
      toast.success("Clinic profile saved.");
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Could not save clinic profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Clinic profile" description="Printed on prescriptions and invoices, and shown in the sidebar/header.">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Clinic name</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Tagline</Label>
          <Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Phone</Label>
          <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input value={form.email} onChange={(e) => set("email", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Registration / license no.</Label>
          <Input value={form.registration_no} onChange={(e) => set("registration_no", e.target.value)} />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Address</Label>
        <Input value={form.address.line} onChange={(e) => setAddr("line", e.target.value)} placeholder="Address line" />
        <div className="grid gap-2 sm:grid-cols-3">
          <Input value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} placeholder="City" />
          <Input value={form.address.state} onChange={(e) => setAddr("state", e.target.value)} placeholder="State" />
          <Input value={form.address.pincode} onChange={(e) => setAddr("pincode", e.target.value)} placeholder="Pincode" />
        </div>
      </div>

      <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save clinic profile"}</Button>
    </SectionCard>
  );
}

function SchedulingTab({ settings, onSaved }) {
  const [form, setForm] = useState(settings.scheduling);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDay = (day) => {
    setForm((f) => ({
      ...f,
      days_open: f.days_open.includes(day) ? f.days_open.filter((d) => d !== day) : [...f.days_open, day].sort(),
    }));
  };

  const addHoliday = () => setForm((f) => ({ ...f, holidays: [...f.holidays, { date: "", label: "" }] }));
  const updateHoliday = (idx, key, value) =>
    setForm((f) => ({ ...f, holidays: f.holidays.map((h, i) => (i === idx ? { ...h, [key]: value } : h)) }));
  const removeHoliday = (idx) => setForm((f) => ({ ...f, holidays: f.holidays.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (form.holidays.some((h) => !h.date)) {
      toast.error("Every holiday needs a date.");
      return;
    }
    setSaving(true);
    try {
      await updateSchedulingSettings(form);
      toast.success("Scheduling rules saved.");
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Could not save scheduling rules.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
    <SectionCard title="OPD scheduling" description="Controls the slot list every doctor's booking calendar is built from.">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label>Slot duration (minutes)</Label>
          <Input type="number" min={5} max={180} value={form.slot_duration_minutes}
            onChange={(e) => set("slot_duration_minutes", Number(e.target.value))} />
        </div>
        <div className="space-y-1.5">
          <Label>Start time</Label>
          <Input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>End time</Label>
          <Input type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} />
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={form.lunch_break_enabled} onCheckedChange={(v) => set("lunch_break_enabled", v)} />
          Lunch break
        </label>
        {form.lunch_break_enabled && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input type="time" value={form.lunch_start} onChange={(e) => set("lunch_start", e.target.value)} />
            <Input type="time" value={form.lunch_end} onChange={(e) => set("lunch_end", e.target.value)} />
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>Days open</Label>
        <div className="flex flex-wrap gap-3">
          {DAYS.map((d) => (
            <label key={d.value} className="flex items-center gap-1.5 text-sm">
              <Checkbox checked={form.days_open.includes(d.value)} onCheckedChange={() => toggleDay(d.value)} />
              {d.label}
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Holidays</Label>
          <button type="button" onClick={addHoliday} className="flex items-center gap-1 text-sm text-primary">
            <Plus className="h-3.5 w-3.5" /> Add holiday
          </button>
        </div>
        {form.holidays.length === 0 && <p className="text-xs text-muted-foreground">No holidays added.</p>}
        {form.holidays.map((h, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <Input type="date" value={h.date} onChange={(e) => updateHoliday(idx, "date", e.target.value)} className="w-40" />
            <Input value={h.label} onChange={(e) => updateHoliday(idx, "label", e.target.value)} placeholder="Label (optional)" />
            <button type="button" onClick={() => removeHoliday(idx)} className="text-muted-foreground hover:text-destructive">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save scheduling rules"}</Button>
    </SectionCard>
    <DoctorSlotDurationSection />
    </div>
  );
}

function DoctorSlotDurationSection() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [duration, setDuration] = useState("");
  const [saving, setSaving] = useState(false);

  const loadDoctors = () => {
    setLoading(true);
    getDoctors({ limit: 200 })
      .then((res) => setDoctors(res.data || []))
      .catch(() => setDoctors([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDoctors();
  }, []);

  const overrides = doctors.filter((d) => d.slot_duration);

  const save = async () => {
    if (!selectedDoctorId) {
      toast.error("Select a doctor first.");
      return;
    }
    const value = Number(duration);
    if (!Number.isInteger(value) || value < 1) {
      toast.error("Enter a valid slot duration in minutes.");
      return;
    }
    setSaving(true);
    try {
      await updateDoctor(selectedDoctorId, { slot_duration: value });
      toast.success("Slot duration updated for this doctor.");
      setSelectedDoctorId("");
      setDuration("");
      loadDoctors();
    } catch (err) {
      toast.error(err?.message || "Could not update.");
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async (doctor) => {
    try {
      await updateDoctor(doctor._id, { slot_duration: null });
      toast.success(`${doctor.name} now uses the clinic default.`);
      loadDoctors();
    } catch (err) {
      toast.error(err?.message || "Could not update.");
    }
  };

  return (
    <SectionCard
      title="Per-doctor slot duration"
      description="Override the clinic-wide slot duration above for a specific doctor — e.g. a specialist who needs longer consultations."
    >
      <div className="grid gap-4 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
        <div className="space-y-1.5">
          <Label>Doctor</Label>
          <ProviderSelect doctors={doctors} value={selectedDoctorId} onChange={setSelectedDoctorId} />
        </div>
        <div className="space-y-1.5">
          <Label>Slot duration (minutes)</Label>
          <Input
            type="number"
            min={1}
            max={180}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="e.g. 20"
          />
        </div>
        <Button onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Set override"}
        </Button>
      </div>

      {!loading && overrides.length > 0 && (
        <div className="space-y-1.5">
          <Label>Current overrides</Label>
          <div className="divide-y divide-border rounded-md border border-border">
            {overrides.map((d) => (
              <div key={d._id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span>
                  {d.name} <span className="text-muted-foreground">— {d.specialty}</span>
                </span>
                <span className="flex items-center gap-3">
                  <span className="font-medium">{d.slot_duration} min</span>
                  <button
                    type="button"
                    onClick={() => clearOverride(d)}
                    className="text-xs text-muted-foreground hover:text-destructive"
                  >
                    Reset to default
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function BillingTab({ settings, onSaved }) {
  const [form, setForm] = useState(settings.billing);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await updateBillingSettings(form);
      toast.success("Billing settings saved.");
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Could not save billing settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Invoicing" description="Controls invoice numbering and whether tax is applied to new invoices.">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label>Invoice prefix</Label>
          <Input value={form.invoice_prefix} onChange={(e) => set("invoice_prefix", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Number padding</Label>
          <Input type="number" min={1} max={10} value={form.invoice_number_padding}
            onChange={(e) => set("invoice_number_padding", Number(e.target.value))} />
        </div>
        <div className="space-y-1.5">
          <Label>Numbering resets</Label>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={form.invoice_reset}
            onChange={(e) => set("invoice_reset", e.target.value)}
          >
            <option value="never">Never</option>
            <option value="yearly">Every calendar year</option>
            <option value="financial_year">Every financial year (Apr–Mar)</option>
          </select>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Next invoice would look like: <span className="font-mono">{form.invoice_prefix}{form.invoice_reset !== "never" ? "2026-" : ""}{"0".repeat(form.invoice_number_padding - 1)}1</span>
      </p>

      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={form.tax_enabled} onCheckedChange={(v) => set("tax_enabled", v)} />
        Apply tax to new invoices
      </label>
      {form.tax_enabled && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Tax label</Label>
            <Input value={form.tax_label} onChange={(e) => set("tax_label", e.target.value)} placeholder="GST" />
          </div>
          <div className="space-y-1.5">
            <Label>Tax rate (%)</Label>
            <Input type="number" min={0} max={100} value={form.tax_rate_percent}
              onChange={(e) => set("tax_rate_percent", Number(e.target.value))} />
          </div>
        </div>
      )}

      <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save billing settings"}</Button>
    </SectionCard>
  );
}

function NotificationsTab({ settings, onSaved }) {
  const [form, setForm] = useState(settings.notifications);
  const [saving, setSaving] = useState(false);
  const toggle = (key) => setForm((f) => ({ ...f, enabled_types: { ...f.enabled_types, [key]: !f.enabled_types[key] } }));

  const save = async () => {
    setSaving(true);
    try {
      await updateNotificationSettings(form);
      toast.success("Notification settings saved.");
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Could not save notification settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Notifications"
      description="Sending is currently a logged simulation (visible on the Messages page) — no real WhatsApp/SMS provider is connected yet. These toggles still control whether that log entry is created at all."
    >
      <div className="space-y-2">
        {NOTIFICATION_TYPES.map((t) => (
          <label key={t.key} className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.enabled_types[t.key]} onCheckedChange={() => toggle(t.key)} />
            {t.label}
          </label>
        ))}
      </div>

      <div className="space-y-1.5">
        <Label>Reminder timing (hours before visit)</Label>
        <Input type="number" min={1} max={168} value={form.reminder_hours_before}
          onChange={(e) => setForm((f) => ({ ...f, reminder_hours_before: Number(e.target.value) }))}
          className="w-32" />
        <p className="text-xs text-muted-foreground">
          Stored for future use — reminders are still only sent by the manual "Send reminder" action today, not automatically.
        </p>
      </div>

      <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save notification settings"}</Button>
    </SectionCard>
  );
}

const VIDEO_PATH_PATTERN = /^\/[\w\-./]+\.(mp4|webm|ogg)$/;

const DISPLAY_MODES = [
  { value: "both", label: "Both", description: "Video loops, interrupted by the live queue on updates (default)" },
  { value: "video", label: "Video only", description: "Always shows the video — the queue never takes over" },
  { value: "queue", label: "Queue only", description: "Always shows the live patient queue — video never plays" },
];

function DisplayTab({ settings, onSaved }) {
  const [mode, setMode] = useState(settings.display?.mode || "both");
  const [savingMode, setSavingMode] = useState(false);
  const savedPageSeconds = settings.display?.page_seconds || 20;
  const [pageSeconds, setPageSeconds] = useState(String(savedPageSeconds));
  const [savingPageSeconds, setSavingPageSeconds] = useState(false);

  const savePageSeconds = async () => {
    const value = Number(pageSeconds);
    if (!Number.isInteger(value) || value < 5 || value > 300) {
      toast.error("Seconds per page must be a whole number between 5 and 300.");
      return;
    }
    setSavingPageSeconds(true);
    try {
      await updateDisplaySettings({ page_seconds: value });
      toast.success(`Each queue page now stays on screen for ${value} seconds.`);
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Could not save seconds per page.");
    } finally {
      setSavingPageSeconds(false);
    }
  };

  const changeMode = async (value) => {
    if (value === mode || savingMode) return;
    const previous = mode;
    setMode(value); // optimistic — reverted below on failure
    setSavingMode(true);
    try {
      await updateDisplaySettings({ mode: value });
      toast.success(`Reception boards now set to "${DISPLAY_MODES.find((m) => m.value === value)?.label}".`);
      onSaved();
    } catch (err) {
      setMode(previous);
      toast.error(err?.message || "Could not change display mode.");
    } finally {
      setSavingMode(false);
    }
  };

  return (
    <SectionCard
      title="Reception board mode"
      description="Controls what every reception TV shows, live — no need to touch the TVs themselves."
    >
      <div className="inline-flex rounded-md border border-input p-1">
        {DISPLAY_MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            disabled={savingMode}
            onClick={() => changeMode(m.value)}
            title={m.description}
            className={`rounded px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
              mode === m.value
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{DISPLAY_MODES.find((m) => m.value === mode)?.description}</p>

      <div className="space-y-2">
        <Label htmlFor="page-seconds">Seconds per queue page</Label>
        <div className="flex max-w-xs gap-2">
          <Input
            id="page-seconds"
            type="number"
            min={5}
            max={300}
            value={pageSeconds}
            onChange={(e) => setPageSeconds(e.target.value)}
          />
          <Button
            type="button"
            onClick={savePageSeconds}
            disabled={savingPageSeconds || Number(pageSeconds) === savedPageSeconds}
          >
            {savingPageSeconds ? "Saving…" : "Save"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          The board shows 4 doctors per page. Each page stays up this long before the next page — or, after the last
          page, the video.
        </p>
      </div>
    </SectionCard>
  );
}

function ReceptionVideoTab({ settings, onSaved }) {
  const savedPath = settings.display?.video_path || "";
  const [input, setInput] = useState(savedPath);
  const [saving, setSaving] = useState(false);

  const previewPath = input === "" || VIDEO_PATH_PATTERN.test(input) ? input : null;

  const save = async () => {
    const videoPath = input.trim();
    if (videoPath !== "" && !VIDEO_PATH_PATTERN.test(videoPath)) {
      toast.error("That doesn't look like a valid video path (e.g. /videos/clinic-tv-ad.mp4).");
      return;
    }
    setSaving(true);
    try {
      await updateDisplaySettings({ video_path: videoPath });
      toast.success(videoPath ? "Video saved — the reception board will pick it up automatically." : "Video removed.");
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Could not save video.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Reception board video"
      description="Loops full-screen on the reception TVs between queue updates. When the video finishes a loop, or the moment a patient is checked in/booked/completed, the board shows the live patient queue (each page for the seconds per queue page set above) before returning to the video."
    >
      <div className="space-y-2">
        <Label htmlFor="video-path">Video file path</Label>
        <div className="flex gap-2">
          <Input
            id="video-path"
            placeholder="/videos/clinic-tv-ad.mp4"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <Button type="button" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Path to a video file placed in medipro-dashboard/public (or leave blank to remove) — the reception board
          just shows the live queue when no video is set.
        </p>
      </div>

      {previewPath ? (
        <div className="aspect-video w-full max-w-md overflow-hidden rounded-md border border-border bg-black">
          <video
            key={previewPath}
            src={`${process.env.PUBLIC_URL}${previewPath}`}
            title="Reception board video preview"
            className="h-full w-full"
            controls
            muted
          />
        </div>
      ) : null}
    </SectionCard>
  );
}

export default function Settings() {
  const dispatch = useDispatch();
  const settings = useSelector((s) => s.settings.data);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    dispatch(getSettingsThunk());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSaved = () => {
    dispatch(getSettingsThunk());
    setReloadKey((k) => k + 1);
  };

  if (!settings) return null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-serif text-2xl font-semibold">Settings</h2>
        <p className="text-sm text-muted-foreground">Clinic-wide configuration — profile, scheduling, billing and notifications</p>
      </div>

      <Tabs defaultValue="clinic">
        <TabsList>
          <TabsTrigger value="clinic">Clinic Profile</TabsTrigger>
          <TabsTrigger value="scheduling">Scheduling</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="display">Reception Display</TabsTrigger>
        </TabsList>
        <TabsContent value="clinic">
          <ClinicTab key={`clinic-${reloadKey}`} settings={settings} onSaved={onSaved} />
        </TabsContent>
        <TabsContent value="scheduling">
          <SchedulingTab key={`scheduling-${reloadKey}`} settings={settings} onSaved={onSaved} />
        </TabsContent>
        <TabsContent value="billing">
          <BillingTab key={`billing-${reloadKey}`} settings={settings} onSaved={onSaved} />
        </TabsContent>
        <TabsContent value="notifications">
          <NotificationsTab key={`notifications-${reloadKey}`} settings={settings} onSaved={onSaved} />
        </TabsContent>
        <TabsContent value="display" className="space-y-4">
          <DisplayTab key={`display-mode-${reloadKey}`} settings={settings} onSaved={onSaved} />
          <ReceptionVideoTab key={`display-video-${reloadKey}`} settings={settings} onSaved={onSaved} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
